import { useNavigate } from "@remix-run/react";
import type { User } from "@supabase/supabase-js";
import { LogOut, Settings2 } from "lucide-react";

import { Avatar, AvatarFallback, AvatarImage } from "~/components/ui/avatar";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { useIsAdmin } from "~/hooks/use-is-admin";
import { handleLogout } from "~/lib/logout";
import { useIsPractitioner, useUserProfile } from "~/lib/user";

import { useSupabaseSession } from "../context/SupabaseSessionContext";
import { useAnonymous } from "../hooks/use-anonymous";
import { useIsMobile } from "../hooks/use-mobile";
import { Button } from "./ui/button";
import { A } from "./ui/typography";

interface UserMenuProps {
    user: User;
}

/**
 * Calculate initials from email
 * If email prefix contains period, use as first/last name
 * Otherwise use first two letters of email prefix
 */
function calculateInitials(email: string): string {
    // Get the part before the @ symbol
    const emailPrefix = email.split("@")[0];

    // If prefix contains period, treat as first.last format
    if (emailPrefix.includes(".")) {
        const [first, last] = emailPrefix.split(".");
        if (first && last) {
            return (first[0] + last[0]).toUpperCase();
        }
    }

    // Otherwise use first two letters of prefix
    return emailPrefix.slice(0, 2).toUpperCase();
}

export function UserMenu() {
    const { data: user } = useUserProfile();
    const navigate = useNavigate();
    const initials = calculateInitials(user?.email ?? "");
    const email = user?.email ?? "";
    const { isAnonymous } = useAnonymous();
    const { data: isAdmin, isLoading: isAdminLoading } = useIsAdmin();

    const handleSignOut = () => handleLogout();

    const avatar = (
        <Avatar className="size-8 cursor-pointer">
            {user?.avatar_url ? (
                <AvatarImage src={user?.avatar_url} alt={email} />
            ) : (
                <AvatarFallback className="bg-rose-100 text-rose-800">
                    {initials}
                </AvatarFallback>
            )}
        </Avatar>
    );

    if (isAnonymous) {
        return (
            <Button
                variant="outline"
                onClick={() => {
                    window.location.href = `/link-account?redirectTo=${encodeURIComponent(
                        window.location.pathname,
                    )}`;
                }}
            >
                Create account
            </Button>
        );
    }

    return (
        <div className="flex items-center gap-4">
            <div className="hidden max-w-[150px] truncate text-xs text-muted-foreground md:block">
                {email}
            </div>
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <div className="flex items-center gap-4">{avatar}</div>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="p-4">
                    {isAdmin && (
                        <>
                            <DropdownMenuItem asChild className="p-2">
                                <A
                                    href="/admin"
                                    className="flex cursor-pointer items-center"
                                >
                                    <Settings2 className="mr-2 size-4" />
                                    Admin
                                </A>
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                        </>
                    )}
                    <DropdownMenuItem asChild className="p-2">
                        <A
                            href="/account"
                            className="flex cursor-pointer items-center"
                        >
                            <Settings2 className="mr-2 size-4" />
                            Settings
                        </A>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                        onClick={handleSignOut}
                        className="flex cursor-pointer items-center p-2"
                    >
                        <LogOut className="mr-2 size-4" />
                        Log out
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
        </div>
    );
}
